import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface PssByStatus {
  [status: string]: number;
}

export interface PastDueSubscription {
  id: string;
  property_name: string;
  org_name: string | null;
  plan_name: string;
  price_cents: number;
  current_period_end: string | null;
  created_at: string;
}

export interface CanceledSubscription {
  id: string;
  property_name: string;
  org_name: string | null;
  plan_name: string;
  price_cents: number;
  cancel_at_period_end: boolean;
  updated_at: string;
}

export interface PendingMissingStripe {
  id: string;
  property_name: string;
  org_name: string | null;
  plan_name: string;
  status: string;
  created_at: string;
}

export interface StripeConnectIncomplete {
  id: string;
  name: string;
  stripe_connect_account_id: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
}

export interface OrgPastDue {
  id: string;
  name: string;
  billing_status: string;
  billing_email: string | null;
  last_payment_failed_at: string | null;
  grace_period_ends_at: string | null;
}

export interface ConnectBillingEvent {
  id: string;
  org_name: string | null;
  property_name: string | null;
  event_type: string;
  total_amount_cents: number | null;
  platform_fee_cents: number | null;
  status: string | null;
  created_at: string;
}

export interface BillingEvent {
  id: string;
  org_name: string | null;
  event_type: string;
  status: string;
  created_at: string;
}

export interface BillingDiagnosticsData {
  pss_by_status: PssByStatus;
  past_due_subscriptions: PastDueSubscription[];
  recent_canceled_subscriptions: CanceledSubscription[];
  pending_missing_stripe: PendingMissingStripe[];
  stripe_connect_incomplete: StripeConnectIncomplete[];
  orgs_past_due: OrgPastDue[];
  recent_connect_events: ConnectBillingEvent[];
  recent_billing_events: BillingEvent[];
}

async function fetchBillingDiagnostics(): Promise<BillingDiagnosticsData> {
  const { data, error } = await supabase.rpc('admin_billing_diagnostics');
  if (error) throw error;
  return data as BillingDiagnosticsData;
}

export function useAdminBillingDiagnostics() {
  return useQuery({
    queryKey: ['admin-billing-diagnostics'],
    queryFn: fetchBillingDiagnostics,
  });
}
